import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { MoonLoader } from "react-spinners";
import { WhitelabelServices } from "../services/WhitelabelServices";
import "../styles/whitelabel-loading.css";

const WhitelabelContext = createContext();

const CACHE_KEY = "whitelabel_config";
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 dias em milissegundos

const DEFAULT_CONFIG = {
  is_whitelabel: false,
  organization_name: "Mercado Agentes",
  site_title:
    "Mercado Agentes - Crie, Venda e Contrate Agentes de IA num só lugar!",
  site_description: "Crie, venda e contrate agentes de IA num só lugar!",
  branding: {
    brand_name: "Mercado Agentes",
    colors: {
      primary: "#3b82f6",
      titles: "#6c757d",
      text: "#000000",
      background: "#ffffff",
      gradient_primary: "linear-gradient(135deg, #3b82f6 0%, #6c757d 100%)",
      gradient_color1: "#3b82f6",
      gradient_color2: "#6c757d",
      selected_gradient: "",
    },
    logo: {
      main: null,
      mobile: null,
      favicon: null,
    },
    domain: {
      custom: null,
      subdomain: null,
      url: null,
    },
    seo: {
      site_title:
        "Mercado Agentes - Crie, Venda e Contrate Agentes de IA num só lugar!",
      site_description: "Crie, venda e contrate agentes de IA num só lugar!",
    },
    sidebar: {
      style: "light",
      bg: "#ffffff",
      text: "#0f172a",
      icon: "#64748b",
      active_bg: null,
      active_text: "#ffffff",
    },
    white_label_enabled: false,
  },
};

// ============================================================
// Utilitários de cor — usados para derivar tokens em runtime
// (paleta RGB para alpha, cor de contraste legível, mistura).
// ============================================================
const hexToRgb = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.trim().replace("#", "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

// Luminância relativa (WCAG) — 0 (preto) a 1 (branco)
const relativeLuminance = ({ r, g, b }) => {
  const srgb = [r, g, b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
};

// Escolhe texto legível (branco ou quase-preto) sobre um fundo hex
const readableTextColor = (bgHex, darkFallback = "#0f172a") => {
  const rgb = hexToRgb(bgHex);
  if (!rgb) return "#ffffff";
  // Contraste com branco vs. com o dark; escolhe o maior
  const lum = relativeLuminance(rgb);
  const contrastWhite = 1.05 / (lum + 0.05);
  const darkRgb = hexToRgb(darkFallback) || { r: 15, g: 23, b: 42 };
  const darkLum = relativeLuminance(darkRgb);
  const contrastDark = (Math.max(lum, darkLum) + 0.05) / (Math.min(lum, darkLum) + 0.05);
  return contrastWhite >= contrastDark ? "#ffffff" : darkFallback;
};

// Mistura duas cores hex (ratio 0..1 de a sobre b)
const mixHex = (aHex, bHex, ratio) => {
  const a = hexToRgb(aHex);
  const b = hexToRgb(bHex);
  if (!a || !b) return aHex;
  const t = Math.max(0, Math.min(1, ratio));
  const ch = (x, y) => Math.round(x * t + y * (1 - t));
  const to2 = (n) => n.toString(16).padStart(2, "0");
  return `#${to2(ch(a.r, b.r))}${to2(ch(a.g, b.g))}${to2(ch(a.b, b.b))}`;
};

const isLightColor = (hex) => {
  const rgb = hexToRgb(hex);
  return rgb ? relativeLuminance(rgb) > 0.5 : true;
};

export const WhitelabelProvider = ({ children }) => {
  const excludedRoutes = [];
  const currentPath = window.location.pathname;
  const shouldLoadWhitelabel = !excludedRoutes.includes(currentPath);
  
  // Verificar cache inicial para reduzir tempo de loading
  const initialCachedConfig = shouldLoadWhitelabel ? (() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = new Date().getTime();
        if (now - timestamp < CACHE_DURATION) {
          return data;
        }
      }
    } catch (error) {
      // Ignorar erros na verificação inicial
    }
    return null;
  })() : null;

  const [config, setConfig] = useState(initialCachedConfig || DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(shouldLoadWhitelabel && !initialCachedConfig);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(!shouldLoadWhitelabel || !!initialCachedConfig);

  const getCachedConfig = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = new Date().getTime();
        if (now - timestamp < CACHE_DURATION) {
          return data;
        }
        localStorage.removeItem(CACHE_KEY);
      }
    } catch (error) {
      console.error("Erro ao ler cache whitelabel:", error);
      localStorage.removeItem(CACHE_KEY);
    }
    return null;
  };

  const setCachedConfig = useCallback((data) => {
    try {
      const cacheData = {
        data,
        timestamp: new Date().getTime(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error("Erro ao salvar cache whitelabel:", error);
    }
  }, []);

  const loadWhitelabelConfig = useCallback(async () => {
    if (hasLoaded) {
      return; // Prevenir múltiplas chamadas
    }

    try {
      setIsLoading(true);
      setError(null);

      const cachedConfig = getCachedConfig();
      if (cachedConfig) {
        setConfig(cachedConfig);
        setIsLoading(false);
        setHasLoaded(true);
        return;
      }

      const response = await WhitelabelServices.getConfig();

      if (response && response.is_whitelabel) {
        setConfig(response);
        setCachedConfig(response);
      } else {
        setConfig(DEFAULT_CONFIG);
        setCachedConfig(DEFAULT_CONFIG);
      }

      setHasLoaded(true);
    } catch (error) {
      setError(error);
      setConfig(DEFAULT_CONFIG);
      setHasLoaded(true);
    } finally {
      setIsLoading(false);
    }
  }, [hasLoaded, setCachedConfig]);

  const applyThemeColors = (colors) => {
    if (colors && typeof colors === "object") {
      const root = document.documentElement;

      // Aplicar cores individuais
      Object.entries(colors).forEach(([key, value]) => {
        // Ignorar valores não-string (ex.: objetos aninhados como sidebar)
        if (value && typeof value === "string") {
          root.style.setProperty(`--whitelabel-${key}`, value);

          // Mapeamentos específicos para compatibilidade
          if (key === "primary") {
            root.style.setProperty(`--primary`, value);
            root.style.setProperty(`--bs-primary`, value);
            root.style.setProperty(`--primary-color`, value);

            // Tokens derivados que color-mix() não cobre:
            // --primary-rgb (para rgba(var(--primary-rgb), a)) e
            // --on-primary (texto legível sobre a cor da marca).
            const rgb = hexToRgb(value);
            if (rgb) {
              root.style.setProperty(`--primary-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
            }
            root.style.setProperty(`--on-primary`, readableTextColor(value));
          }
          if (key === "titles") {
            root.style.setProperty(`--secondary`, value);
            root.style.setProperty(`--titles-color`, value);
            root.style.setProperty(`--bs-secondary`, value);
          }
          if (key === "text") {
            root.style.setProperty(`--text-color`, value);
            root.style.setProperty(`--bs-body-color`, value);
            root.style.setProperty(`--body-color`, value);
          }
          if (key === "background") {
            root.style.setProperty(`--background-color`, value);
            root.style.setProperty(`--bs-body-bg`, value);
            root.style.setProperty(`--body-bg`, value);
          }
        }
      });

      // Aplicar gradiente se existir
      if (colors.gradient_primary) {
        root.style.setProperty(`--gradient-primary`, colors.gradient_primary);
      }

      // Aplicar cores do gradiente individualmente
      if (colors.gradient_color1) {
        root.style.setProperty(`--gradient-color1`, colors.gradient_color1);
      }
      if (colors.gradient_color2) {
        root.style.setProperty(`--gradient-color2`, colors.gradient_color2);
      }
    }
  };

  const updateDocumentTitle = (siteTitle) => {
    if (
      siteTitle &&
      siteTitle !==
        "Mercado Agentes - Crie, Venda e Contrate Agentes de IA num só lugar!"
    ) {
      document.title = siteTitle;
    }
  };

  const updateMetaTags = (siteTitle, siteDescription, logoUrl) => {
    let currentUrl;
    try {
      currentUrl = window.location.origin + window.location.pathname;
    } catch (error) {
      console.warn("Erro ao obter URL atual:", error);
      currentUrl = window.location.href;
    }

    // Função auxiliar para criar ou atualizar meta tag
    const updateOrCreateMeta = (selector, content, isProperty = false) => {
      if (!content || typeof content !== "string") {
        return; // Não fazer nada se content for inválido
      }

      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement("meta");
        const attrName = isProperty ? "property" : "name";
        // Extrair o valor do atributo de forma mais segura
        const match = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (match && match[2]) {
          meta.setAttribute(attrName, match[2]);
          document.head.appendChild(meta);
        } else {
          console.warn("Selector inválido para meta tag:", selector);
          return;
        }
      }
      meta.setAttribute("content", content);
    };

    // Atualizar meta description
    if (siteDescription) {
      updateOrCreateMeta('meta[name="description"]', siteDescription);
    }

    // Atualizar Open Graph tags
    if (siteTitle) {
      updateOrCreateMeta('meta[property="og:title"]', siteTitle, true);
    }
    if (siteDescription) {
      updateOrCreateMeta(
        'meta[property="og:description"]',
        siteDescription,
        true
      );
    }
    updateOrCreateMeta('meta[property="og:url"]', currentUrl, true);

    // Atualizar Twitter tags
    if (siteTitle) {
      updateOrCreateMeta('meta[name="twitter:title"]', siteTitle);
    }
    if (siteDescription) {
      updateOrCreateMeta('meta[name="twitter:description"]', siteDescription);
    }
    updateOrCreateMeta('meta[name="twitter:url"]', currentUrl);

    // Atualizar imagens de compartilhamento com logo se disponível
    if (logoUrl) {
      updateOrCreateMeta('meta[property="og:image"]', logoUrl, true);
      updateOrCreateMeta('meta[property="og:image:secure_url"]', logoUrl, true);
      updateOrCreateMeta('meta[name="twitter:image"]', logoUrl);

      // Atualizar alt text das imagens
      if (siteTitle) {
        updateOrCreateMeta(
          'meta[property="og:image:alt"]',
          `${siteTitle} - Plataforma de Agentes de IA`,
          true
        );
        updateOrCreateMeta(
          'meta[name="twitter:image:alt"]',
          `${siteTitle} - Plataforma de Agentes de IA`
        );
      }
    }
  };

  const updateFavicon = (faviconUrl) => {
    if (faviconUrl) {
      const link =
        document.querySelector("link[rel~='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "icon";
      link.href = faviconUrl;
      document.getElementsByTagName("head")[0].appendChild(link);
    }
  };

  const applyCssCustomizations = (cssCustomizations) => {
    if (cssCustomizations && typeof cssCustomizations === "object") {
      const root = document.documentElement;

      // Aplicar logo_width se existir
      if (cssCustomizations.logo_width) {
        root.style.setProperty(
          `--whitelabel-logo-width`,
          cssCustomizations.logo_width
        );

        // Aplicar diretamente na classe whitelabel-logo-main
        const style =
          document.getElementById("whitelabel-logo-style") ||
          document.createElement("style");
        style.id = "whitelabel-logo-style";
        style.innerHTML = `
          .whitelabel-logo-main {
            width: ${cssCustomizations.logo_width};
            max-width: ${cssCustomizations.logo_width} !important;
          }
        `;
        document.head.appendChild(style);
      }
    }
  };

  // Aplica os tokens de sidebar (--sidebar-*). Quando active_bg não é
  // definido, usa o gradiente da marca. Texto/ícone/hover são derivados
  // do fundo por contraste quando não vêm explícitos da config.
  const applySidebarTokens = (sidebar, colors) => {
    const root = document.documentElement;
    if (!sidebar || typeof sidebar !== "object") return;

    const bg = sidebar.bg || "#ffffff";
    const text = sidebar.text || readableTextColor(bg);
    const icon = sidebar.icon || mixHex(text, bg, 0.65);

    root.style.setProperty("--sidebar-bg", bg);
    root.style.setProperty("--sidebar-text", text);
    root.style.setProperty("--sidebar-icon", icon);
    root.style.setProperty("--sidebar-muted", mixHex(text, bg, 0.45));
    root.style.setProperty("--sidebar-border", mixHex(text, bg, 0.12));

    // Hover: leve realce sobre o fundo (fundo claro escurece, escuro clareia)
    const hover = isLightColor(bg)
      ? mixHex("#000000", bg, 0.05)
      : mixHex("#ffffff", bg, 0.08);
    root.style.setProperty("--sidebar-hover-bg", hover);

    // Item ativo: cor explícita, senão a cor primária (sólida) da marca.
    const activeBg = sidebar.active_bg || (colors && colors.primary) || null;
    if (activeBg) {
      root.style.setProperty("--sidebar-active-bg", activeBg);
      root.style.setProperty(
        "--sidebar-active-text",
        sidebar.active_text || readableTextColor(activeBg)
      );
    } else {
      // Sem cor definida: cai no default do :root (var(--primary))
      root.style.setProperty(
        "--sidebar-active-bg",
        "var(--primary)"
      );
      root.style.setProperty(
        "--sidebar-active-text",
        sidebar.active_text || "#ffffff"
      );
    }
  };

  useEffect(() => {
    if (!hasLoaded && shouldLoadWhitelabel) {
      loadWhitelabelConfig();
    }
  }, [hasLoaded, loadWhitelabelConfig, shouldLoadWhitelabel]);

  // Listener para eventos de atualização do cache
  useEffect(() => {
    const handleCacheUpdate = (event) => {
      // Recarregar configurações do cache atualizado
      try {
        const cachedConfig = getCachedConfig();
        
        if (cachedConfig) {
          setConfig(cachedConfig);
        }
      } catch (error) {
        console.error('Erro ao recarregar configurações do cache:', error);
      }
    };

    // Adicionar listener para o evento customizado
    window.addEventListener('whitelabel-cache-updated', handleCacheUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('whitelabel-cache-updated', handleCacheUpdate);
    };
  }, []);

  useEffect(() => {
    if (!isLoading && config && hasLoaded) {
      const colors = WhitelabelServices.getColors(config);
      const logos = WhitelabelServices.getLogos(config);
      const siteTitle =
        config.branding?.seo?.site_title || DEFAULT_CONFIG.site_title;
      const siteDescription =
        config.branding?.seo?.site_description ||
        DEFAULT_CONFIG.site_description;
      const logoUrl = logos.main || logos.mobile;
      const cssCustomizations =
        config.branding?.css_customizations || config.css_customizations;

      const sidebar = WhitelabelServices.isWhitelabel(config)
        ? config.branding?.sidebar
        : null;

      applyThemeColors(colors);
      applySidebarTokens(sidebar, colors);
      updateDocumentTitle(siteTitle);
      updateFavicon(logos.favicon);
      updateMetaTags(siteTitle, siteDescription, logoUrl);
      applyCssCustomizations(cssCustomizations);
    }
  }, [config, isLoading, hasLoaded]);

  const refresh = useCallback(() => {
    setHasLoaded(false);
  }, []);

  const clearCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    setHasLoaded(false);
  }, []);

  const contextValue = useMemo(
    () => {
      const logos = WhitelabelServices.getLogos(config) || DEFAULT_CONFIG.branding.logo;
      const colors = WhitelabelServices.getColors(config) || DEFAULT_CONFIG.branding.colors;
      
      return {
        config,
        isLoading,
        error,
        isWhitelabel: WhitelabelServices.isWhitelabel(config),
        branding:
          WhitelabelServices.getBranding(config) || DEFAULT_CONFIG.branding,
        colors,
        logos,
        brandName: WhitelabelServices.getBrandName(config),
        organizationName: WhitelabelServices.getOrganizationName(config),
        siteTitle: config.branding?.seo?.site_title || DEFAULT_CONFIG.site_title,
        siteDescription:
          config.branding?.seo?.site_description ||
          DEFAULT_CONFIG.site_description,
        refresh,
        clearCache,
      };
    },
    [config, isLoading, error, refresh, clearCache]
  );

  return (
    <WhitelabelContext.Provider value={contextValue}>
      {isLoading ? (
        <div className="whitelabel-loading-overlay">
          <MoonLoader color="var(--primary, #3b82f6)" size={50} />
          
        </div>
      ) : (
        children
      )}
    </WhitelabelContext.Provider>
  );
};

export const useWhitelabel = () => {
  const context = useContext(WhitelabelContext);
  if (!context) {
    throw new Error(
      "useWhitelabel deve ser usado dentro de WhitelabelProvider"
    );
  }
  return context;
};

export default WhitelabelContext;
